/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { MemberService } from 'app/cd/services/member-service';
import { Toaster } from 'app/shared/lib/toaster';
import { IProviderSearchOptions } from 'app/cd/elements/providers/provider-search-filters';
import { deepClone } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { ProviderLocationSelect } from 'app/cd/elements/providers/provider-location-select';
import { I18N } from 'aurelia-i18n';
import { EssAutocompleteCustomElement } from 'app/shared/elements/forms/ess-autocomplete';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class SaveMemberProvider extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'reload' })
    protected memberProvider: CD.MemberProvider | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberId: string | null = null;

    protected editedMemberProvider: CD.MemberProvider | null = null;
    protected editedMemberPlan: CD.MemberPlan | null = null;
    protected options: IProviderSearchOptions | null = null;
    protected relationships: ReadonlyArray<CD.MemberProviderRelationship> = [];

    protected memberPlanSearchQuery: string | null = null;   // the value of the autocomplete text box
    protected memberPlanSearchResults: CD.MemberPlan[] = [];  // array of search results from API for autocomplete


    ////****************** Element References ***************************************////
    protected providerSelect: ProviderLocationSelect | null = null;
    protected $planAutocomplete!: EssAutocompleteCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        if(this.memberProvider) {
            this.editedMemberProvider = deepClone(this.memberProvider);
            this.editedMemberPlan = this.memberProvider.member?.plan || null;
            if(this.editedMemberPlan) {
                this.memberPlanSearchQuery = this.editedMemberPlan.healthPlan && this.editedMemberPlan.lineOfBusiness ?
                    `${this.editedMemberPlan.healthPlan.$display} - ${this.editedMemberPlan.lineOfBusiness.$display}` : null;
            }
        } else {
            this.editedMemberProvider = null;
        }

        await this.loadMemberProviderRelationships();
    }

    ////****************** Functions ************************************************////
    /** Called when a user clicks on an auto-complete search result */
    protected addMemberPlan(memberPlan: CD.MemberPlan): void {
        this.editedMemberPlan = deepClone(memberPlan);
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        this.editedMemberProvider = null;
        this.editedMemberPlan = null;
        this.memberPlanSearchResults = [];
        this.memberPlanSearchQuery = null;
        if(this.providerSelect) this.providerSelect.clear();
    }

    public async submit(): Promise<boolean> {
        return this.saveMemberProvider();
    }

    protected buildMemberProvider(): CD.MemberProvider {
        const memberProvider: CD.MemberProvider = {
            id: this.editedMemberProvider?.id,
            member: {
                id: this.memberId,
                plan: getReference(this.editedMemberPlan)
            },
            provider: {
                id: this.editedMemberProvider?.provider?.location?.provider?.id,
                location: getReference(this.editedMemberProvider?.provider?.location)
            },
            relationship: getReference(this.editedMemberProvider?.relationship),
            dateRange: {
                start: this.editedMemberProvider?.dateRange?.start,
                end: this.editedMemberProvider?.dateRange?.end
            }
        };

        return memberProvider;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async searchMemberPlans(searchValue: string): Promise<void> {
        // NOTE: critical to reset autocomplete to re-render if resultant objects are the same as the previous search
        this.memberPlanSearchResults = [];
        //NOTE: critical to ensure validation works correctly when autocomplete values are emptied out or typos entered
        this.$planAutocomplete.value = null;
        // character limit : min of 2 characters to initiate search // for performance enhancement
        if(searchValue.length < 2) {
            return;
        }
        if(!this.memberId) return;

        try {
            this.state.beginLoad();

            const includes = [
                'healthPlan',
                'lineOfBusiness'
            ];

            const response = await this.memberService.searchMemberPlansWithId(this.memberId, searchValue, { top: 30 }, includes);
            this.memberPlanSearchResults = response.results;
            if(this.memberPlanSearchResults.length === 0) {
                this.toaster.warn().message(this.i18n.tr('ui:messages.events.noResults')).show();
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveMemberProvider(): Promise<boolean> {
        if(!this.editedMemberProvider) return false;

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.memberService.saveMemberProvider(this.buildMemberProvider());
            isSuccessful = response.isSuccessful;

            return isSuccessful;
        }
        catch(error) {
            // if duplicate constraint violated on member provider insert/update, show toast
            if(error.status.errors) {
                const invalidId = error.status.errors.some((err: any) => err.code === 'property.constraintViolated' && err.property === null);
                if(invalidId) this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.message', { what: this.i18n.tr('cd:memberProviderRelationship.$') })).show();
            }
            throw error;
        }
        finally {
            if(this.editedMemberProvider.id) {
                this.toaster.standard.updated('cd:memberProviderRelationship.$', isSuccessful);
            } else {
                this.toaster.standard.created('cd:memberProviderRelationship.$', isSuccessful);
            }
            this.state.endSave();
        }
    }

    @apiCall
    private async loadMemberProviderRelationships(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMemberProviderRelationships();
            this.relationships = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
